/* eslint-disable @typescript-eslint/no-explicit-any */
import IconColumns from '@assets/icons/columns.svg?react';
import IconDelete from '@assets/icons/delete.svg?react';
import IconExcel from '@assets/icons/excel.svg?react';
import IconFilters from '@assets/icons/filters.svg?react';
import IconLock from '@assets/icons/lock.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import {
 Badge,
 Box,
 Button,
 Grid,
 Grid2,
 IconButton,
 Typography,
 useTheme,
} from '@mui/material';
import {
 gridFilterModelSelector,
 GridSlots,
 PropsFromSlot,
 useGridApiContext,
 useGridSelector,
} from '@mui/x-data-grid-premium';
import {
 Dispatch,
 MutableRefObject,
 SetStateAction,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import { useFieldsConfig } from '../../../contexts/useFieldsConfigDialog';
import { usePage } from '../../../contexts/usePageContext';
import {
 ColPresetType,
 FilterPresetType,
 PageNameType,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import reduxSlicesList from '../../../redux/reduxSlicesList';
import {
 approveResultStart,
 deleteItemStart,
} from '../../../redux/results/resultsSlice';
import { RootState } from '../../../redux/store';
import { downloadBase64File } from '../../../utils/base64File';
import ToolbarReports from './ToolbarReports';
import { CustomGridApi, TableModalState } from './types';
import { prepareFilters } from './utils';

interface CustomCalendarHeaderProps
 extends PropsFromSlot<GridSlots['toolbar']> {}

// removed memo() because of apiRef update

const isPresetChanged = (
 presets: ColPresetType[] | null,
 currentPreset: ColPresetType | FilterPresetType,
) =>
 JSON.stringify(
  presets?.find(
   (item: ColPresetType | FilterPresetType) =>
    item.name === currentPreset?.name,
  ),
 ) !== JSON.stringify(currentPreset);

const GridToolbar = ({}: CustomCalendarHeaderProps) => {
 console.log('GridToolbar');
 const dispatch = useAppDispatch();
 const { t } = useTranslation();
 const { pageName } = usePage();
 const th = useTheme();
 const slice = reduxSlicesList[pageName as keyof typeof reduxSlicesList];

 const selector = useAppSelector(
  (state: RootState) => state[pageName as PageNameType],
 );
 const { item, structure, success, loading, excel } = selector;

 const apiRef = useGridApiContext() as MutableRefObject<CustomGridApi>;
 const apiCurrent = apiRef.current;
 const { setColModalOpen } = apiCurrent;
 const [selectedRows, setSelectedRows] = useState<Array<number>>();
 const rowCount = apiCurrent.getRowsCount();

 const { openDialog: openConfirmationDialog } = useConfirmationDialog();

 const gridSelector = useGridSelector(apiRef, gridFilterModelSelector);

 const handleGetExcel = () => {
  const filters = prepareFilters(
   apiRef.current.state.filter.filterModel.items,
   structure,
  );
  dispatch(slice.getTableExcelStart({ table: pageName, ...filters }));
 };

 useEffect(() => {
  if (success?.getTableExcel) {
   if (excel?.file && excel?.type && excel?.extension && excel?.name) {
    downloadBase64File(excel?.file, excel?.name, excel?.type, excel?.extension);
   }
  }
 }, [success]);

 const filtersCount = useMemo(() => {
  const filterItems = gridSelector.items;
  const uniqueFilters = new Set();

  // Iterate through each object in the array
  for (const i of filterItems) {
   // Add the field value to the Set if it exists
   if (i.field !== undefined) {
    uniqueFilters.add(i.field);
   }
  }

  // Return the size of the Set (number of unique fields)
  return uniqueFilters.size;
 }, [gridSelector.items]);

 const { presetsColumns, currentColumnsPreset } = useSelector(
  (state: RootState) => state.clientConfiguration,
 );

 const isPreset =
  !currentColumnsPreset || !presetsColumns
   ? false
   : isPresetChanged(
      presetsColumns?.[pageName],
      currentColumnsPreset?.[pageName] as ColPresetType,
     );

 useEffect(() => {
  const handleSelectionChange = () => {
   const rows = apiCurrent.getSelectedRows();
   setSelectedRows(
    Array.from(rows.values())
     .filter((data) => data?.Id) // for filtering out grouping rows
     .map((data) => data.Id),
   );
  };

  // Listen for selection changes
  const unsubscribe = apiCurrent.subscribeEvent(
   'rowSelectionChange',
   handleSelectionChange,
  );

  return () => unsubscribe();
 }, [apiRef]);

 return (
  <Box sx={{ px: '20px', py: { xs: '16px', md: '24px' } }}>
   <Grid2 container spacing={2} sx={{}}>
    <Grid2
     size={{ sm: 6, xl: 4 }}
     sx={{ display: 'flex', height: '40px', gap: 2 }}
    >
     <Box
      sx={{
       display: { xs: 'flex', md: 'none', alignItems: 'center', gap: '12px' },
      }}
     >
      <IconButton onClick={() => apiCurrent.setCreateModalOpen(true)}>
       <IconPlus
        style={{
         color: th.palette.kemek.blue,
         transition: 'all 0.3s ease-in-out',
        }}
       />
      </IconButton>
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
       {t(`entities.${pageName}`)} ({rowCount})
      </Typography>
     </Box>

     <Button
      variant="outlined"
      sx={{ display: { xs: 'none', md: 'flex' } }}
      endIcon={
       <IconPlus
        style={{
         color: th.palette.kemek.blue,
         transition: 'all 0.3s ease-in-out',
        }}
       />
      }
      onClick={() => apiCurrent.setCreateModalOpen(true)}
     >
      {t('gridToolbar.create')}
     </Button>
     <Button
      variant="outlined"
      color="error"
      sx={{ display: { xs: 'none', md: 'flex' } }}
      endIcon={
       <IconDelete
        style={{
         color: !selectedRows?.length
          ? th.palette.kemek.lightGray
          : th.palette.kemek.red,
         transition: 'all 0.3s ease-in-out',
        }}
       />
      }
      onClick={() => {
       openConfirmationDialog({
        title: t('global.delete'),
        message: t('global.deleteMessage'),
        onConfirm: () => {
         dispatch(deleteItemStart({ table: pageName, ids: selectedRows })); // This is a redux action
        },
       });
      }}
      disabled={!selectedRows?.length}
     >
      {t('global.delete')}
     </Button>
     <Button
      variant="outlined"
      color="secondary"
      sx={{ display: { xs: 'none', md: 'flex' } }}
      endIcon={
       <IconLock
        style={{
         color: !selectedRows?.length
          ? th.palette.kemek.lightGray
          : th.palette.kemek.darkGray,
         transition: 'all 0.3s ease-in-out',
        }}
       />
      }
      onClick={() => {
       openConfirmationDialog({
        title: t('global.block'),
        message: t('global.blockMessage'),
        buttonText: t('global.block'),
        buttonProps: {
         color: 'primary',
         variant: 'contained',
         endIcon: <IconLock style={{ color: 'white' }} />,
        },
        onConfirm: () => {
         dispatch(approveResultStart(selectedRows)); // This is a redux action
        },
       });
      }}
      disabled={!selectedRows?.length}
     >
      {t('global.block')}
     </Button>
     {/* <IconButton
      aria-label="datagrid-more-menu"
      color="primary"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
       setMoreMenuAnchor(e.currentTarget)
      }
     >
      <IconDots />
     </IconButton>
     <Menu
      MenuListProps={{ disablePadding: true }}
      anchorEl={moreMenuAnchor}
      open={!!moreMenuAnchor}
      onClose={() => setMoreMenuAnchor(null)}
     >
      <MenuItem
       onClick={() => {
        localStorage.clear();
        window.location.reload();
       }}
      >
       <ListItemIcon>
        <LazySvg
         name="delete"
         styles={{
          '& path': {
           stroke: customColors.red,
          },
         }}
        />
       </ListItemIcon>
       <Typography variant="inherit">clear localStorage</Typography>
      </MenuItem>
     </Menu> */}
    </Grid2>
    <Grid2
     size={{ sm: 6, xl: 4 }}
     offset={{ sm: 0, xl: 4 }}
     sx={{
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      justifyContent: 'flex-end',
      gap: 2,
      ml: 'auto',
     }}
    >
     <ToolbarReports />
     <Button
      variant="outlined"
      endIcon={<IconExcel />}
      // onClick={() => apiCurrent.exportDataAsExcel()}
      onClick={() => handleGetExcel()}
      disabled={!!loading?.getTableExcel}
      sx={{
       display: { xs: 'none', md: 'flex' },
       p: 1,
       text: 'red',
       minWidth: '40px',
       '.MuiButton-icon': { m: 0 },
      }}
     />
     {/* <IconButton
      aria-label=""
      color="success"
      sx={{ width: 36, height: 36, display: { xs: 'none', md: 'flex' } }}
      onClick={() => openFieldsConfigDialog({ type: 'sidebar' })}
     >
      <IconGraph />
     </IconButton> */}
     <IconButton
      aria-label=""
      color="success"
      onClick={() => {
       apiCurrent.setFilterSidebarOpen((prev) => !prev);
      }}
      sx={{ width: 36, height: 36 }}
     >
      {filtersCount > 0 ? (
       <Badge badgeContent={filtersCount} color="primary">
        <IconFilters style={{ color: th.palette.kemek.blue }} />
       </Badge>
      ) : (
       <IconFilters style={{ color: th.palette.kemek.blue }} />
      )}
     </IconButton>
     <IconButton
      aria-label=""
      color="success"
      onClick={() => setColModalOpen(true)}
      sx={{ width: 36, height: 36 }}
     >
      {isPreset ? (
       <Badge badgeContent="!" color="primary">
        <IconColumns style={{ color: th.palette.kemek.blue }} />
       </Badge>
      ) : (
       <IconColumns style={{ color: th.palette.kemek.blue }} />
      )}
     </IconButton>
    </Grid2>
   </Grid2>
  </Box>
 );
};
GridToolbar.displayName = 'GridToolbar';
export default GridToolbar;
